import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import pendenciasReportService, {
  PendenciaPlano,
  RelatorioComissaoPendente,
  RelatorioEmendaDevolvidaComCiencia,
  RelatorioEmendaDevolvidaSemCiencia,
  RelatorioGestorPendente,
} from '../services/pendenciasReportService';
import { UI_THEME } from '../ui/theme';
import RelatorioSecretariasSemPlanoPage from './RelatorioSecretariasSemPlanoPage';

type TabKey = 'gestores' | 'comissoes' | 'devolvidas' | 'com-ciencia' | 'secretarias-sem-plano';

const TABS: { key: TabKey; label: string }[] = [
  { key: 'gestores', label: 'Gestores' },
  { key: 'comissoes', label: 'Comissões' },
  { key: 'devolvidas', label: 'Devolvidas sem Ciência' },
  { key: 'com-ciencia', label: 'Devolvidas com Ciência' },
  { key: 'secretarias-sem-plano', label: 'Secretarias sem plano' },
];

const isTabKey = (value?: string): value is TabKey =>
  value === 'gestores'
  || value === 'comissoes'
  || value === 'devolvidas'
  || value === 'com-ciencia'
  || value === 'secretarias-sem-plano';

const getErrorMessage = (err: unknown, fallback: string): string => {
  if (typeof err === 'object' && err !== null && 'response' in err) {
    const resp = (err as { response?: { data?: { error?: string } } }).response;
    if (resp?.data?.error) return resp.data.error;
  }
  return fallback;
};

const formatCurrency = (value?: number | null) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value ?? 0);

const formatDate = (value?: string | null) => {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? '—'
    : new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short' }).format(date);
};

const isSaas = UI_THEME === 'saas';
const primaryButtonClass = isSaas
  ? 'inline-flex items-center justify-center rounded-lg bg-teal-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-teal-700 disabled:cursor-not-allowed disabled:opacity-60 dark:bg-teal-500 dark:hover:bg-teal-400'
  : 'inline-flex items-center justify-center rounded-lg bg-blue-700 px-4 py-2 text-sm font-semibold text-white transition hover:bg-blue-800 disabled:cursor-not-allowed disabled:opacity-60 dark:bg-blue-600 dark:hover:bg-blue-500';
const secondaryButtonClass = 'inline-flex items-center justify-center rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800';
const activeTabClass = isSaas
  ? 'border-b-2 border-teal-600 text-teal-700 dark:border-teal-400 dark:text-teal-300'
  : 'border-b-2 border-blue-600 text-blue-700 dark:border-blue-400 dark:text-blue-300';

const cardClass = 'overflow-hidden rounded-2xl border border-slate-200 dark:border-slate-700';
const messageBoxClass = 'rounded-xl border border-slate-200 px-4 py-10 text-center text-sm text-slate-500 dark:border-slate-700 dark:text-slate-400';
const errorBoxClass = 'rounded-xl border border-red-200 bg-red-50 px-4 py-6 text-sm text-red-700 dark:border-red-900/60 dark:bg-red-950/30 dark:text-red-300';
const thClass = 'border-b border-slate-200 bg-slate-50 px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-600 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300';
const tdClass = 'border-b border-slate-100 px-3 py-2 text-sm text-slate-700 dark:border-slate-800 dark:text-slate-200';

const DiasBadge: React.FC<{ dias: number }> = ({ dias }) => {
  const tone =
    dias >= 15
      ? 'bg-red-100 text-red-700 dark:bg-red-950/40 dark:text-red-300'
      : dias >= 7
        ? 'bg-amber-100 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300'
        : 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300';
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold ${tone}`}>
      {dias} {dias === 1 ? 'dia' : 'dias'}
    </span>
  );
};

const PlanosTable: React.FC<{ planos: PendenciaPlano[] }> = ({ planos }) => (
  <div className="overflow-x-auto">
    <table className="w-full border-collapse">
      <thead>
        <tr>
          <th className={thClass}>Emenda</th>
          <th className={thClass}>Instituição</th>
          <th className={thClass}>Valor</th>
          <th className={thClass}>Parado desde</th>
          <th className={thClass}>Dias parado</th>
        </tr>
      </thead>
      <tbody>
        {planos.map((p) => (
          <tr key={p.planoId}>
            <td className={tdClass}>{p.emendaCodigo || '—'}</td>
            <td className={tdClass}>{p.instituicaoNome || '—'}</td>
            <td className={tdClass}>{formatCurrency(p.valor)}</td>
            <td className={tdClass}>{formatDate(p.desde)}</td>
            <td className={tdClass}><DiasBadge dias={p.diasParado} /></td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const RelatorioPendenciasPage: React.FC = () => {
  const { tab } = useParams<{ tab?: string }>();
  const navigate = useNavigate();
  const activeTab: TabKey = isTabKey(tab) ? tab : 'gestores';

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [exporting, setExporting] = useState<'xlsx' | 'pdf' | null>(null);
  const [gestores, setGestores] = useState<RelatorioGestorPendente[]>([]);
  const [comissoes, setComissoes] = useState<RelatorioComissaoPendente[]>([]);
  const [devolvidas, setDevolvidas] = useState<RelatorioEmendaDevolvidaSemCiencia[]>([]);
  const [comCiencia, setComCiencia] = useState<RelatorioEmendaDevolvidaComCiencia[]>([]);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      if (activeTab === 'secretarias-sem-plano') {
        return;
      } else if (activeTab === 'gestores') {
        setGestores(await pendenciasReportService.getGestores());
      } else if (activeTab === 'comissoes') {
        setComissoes(await pendenciasReportService.getComissoes());
      } else if (activeTab === 'devolvidas') {
        setDevolvidas(await pendenciasReportService.getDevolvidas());
      } else {
        setComCiencia(await pendenciasReportService.getDevolvidasComCiencia());
      }
    } catch (err) {
      setError(getErrorMessage(err, 'Não foi possível carregar o relatório de pendências.'));
    } finally {
      setLoading(false);
    }
  }, [activeTab]);

  useEffect(() => {
    load();
  }, [load]);

  const handleExport = async (type: 'xlsx' | 'pdf') => {
    try {
      setExporting(type);
      setError('');
      if (activeTab === 'gestores') {
        await (type === 'xlsx'
          ? pendenciasReportService.exportGestoresXlsx()
          : pendenciasReportService.exportGestoresPdf());
      } else if (activeTab === 'comissoes') {
        await (type === 'xlsx'
          ? pendenciasReportService.exportComissoesXlsx()
          : pendenciasReportService.exportComissoesPdf());
      } else if (activeTab === 'devolvidas') {
        await (type === 'xlsx'
          ? pendenciasReportService.exportDevolvidasXlsx()
          : pendenciasReportService.exportDevolvidasPdf());
      } else {
        await (type === 'xlsx'
          ? pendenciasReportService.exportDevolvidasComCienciaXlsx()
          : pendenciasReportService.exportDevolvidasComCienciaPdf());
      }
    } catch (err) {
      setError(getErrorMessage(err, `Não foi possível exportar o arquivo ${type.toUpperCase()}.`));
    } finally {
      setExporting(null);
    }
  };

  const totalGestores = gestores.reduce((acc, g) => acc + g.qtdPlanos, 0);
  const totalComissoes = comissoes.reduce((acc, m) => acc + m.qtdPlanos, 0);

  return (
    <div className="space-y-5">
      <div className="flex flex-col gap-1">
        <h1 className="text-xl font-bold text-slate-900 dark:text-white">Relatório de Pendências</h1>
        <p className="text-sm text-slate-500 dark:text-slate-400">
          Acompanhe responsáveis pendentes e a situação da ciência nas emendas devolvidas.
        </p>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 dark:border-slate-700">
        <nav className="flex max-w-full gap-1 overflow-x-auto" aria-label="Abas de pendências">
          {TABS.map((t) => {
            const isActive = t.key === activeTab;
            return (
              <button
                key={t.key}
                type="button"
                onClick={() => navigate(`/dashboard/relatorios/pendencias/${t.key}`)}
                className={`whitespace-nowrap rounded-t-lg px-4 py-2 text-sm font-medium transition ${
                  isActive
                    ? activeTabClass
                    : 'text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200'
                }`}
                aria-current={isActive ? 'page' : undefined}
              >
                {t.label}
              </button>
            );
          })}
        </nav>
        {activeTab !== 'secretarias-sem-plano' && <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => handleExport('xlsx')}
            disabled={exporting !== null || loading}
            className={primaryButtonClass}
          >
            {exporting === 'xlsx' ? 'Exportando...' : 'Exportar XLSX'}
          </button>
          <button
            type="button"
            onClick={() => handleExport('pdf')}
            disabled={exporting !== null || loading}
            className={secondaryButtonClass}
          >
            {exporting === 'pdf' ? 'Exportando...' : 'Exportar PDF'}
          </button>
        </div>}
      </div>

      {error && <div className={errorBoxClass}>{error}</div>}

      {loading ? (
        <div className={messageBoxClass}>Carregando relatório...</div>
      ) : activeTab === 'secretarias-sem-plano' ? (
        <RelatorioSecretariasSemPlanoPage embedded />
      ) : activeTab === 'gestores' ? (
        gestores.length === 0 ? (
          <div className={messageBoxClass}>Nenhum gestor com planos pendentes.</div>
        ) : (
          <div className="space-y-4">
            <p className="text-sm text-slate-500 dark:text-slate-400">
              {gestores.length} gestor(es) · {totalGestores} plano(s) pendente(s)
            </p>
            {gestores.map((g) => (
              <div key={g.gestorId ?? 'sem-gestor'} className={cardClass}>
                <div className="flex flex-wrap items-center justify-between gap-2 bg-slate-50 px-4 py-3 dark:bg-slate-800">
                  <div>
                    <div className="font-semibold text-slate-900 dark:text-white">{g.gestorNome}</div>
                    {g.secretaria && (
                      <div className="text-xs text-slate-500 dark:text-slate-400">{g.secretaria}</div>
                    )}
                  </div>
                  <span className="rounded-full bg-slate-200 px-2.5 py-0.5 text-xs font-semibold text-slate-700 dark:bg-slate-700 dark:text-slate-200">
                    {g.qtdPlanos} plano(s)
                  </span>
                </div>
                <PlanosTable planos={g.planos} />
              </div>
            ))}
          </div>
        )
      ) : activeTab === 'comissoes' ? (
        comissoes.length === 0 ? (
          <div className={messageBoxClass}>Nenhum membro de comissão com parecer pendente.</div>
        ) : (
          <div className="space-y-4">
            <p className="text-sm text-slate-500 dark:text-slate-400">
              {comissoes.length} membro(s) · {totalComissoes} parecer(es) pendente(s)
            </p>
            {comissoes.map((m) => (
              <div key={m.usuarioId} className={cardClass}>
                <div className="flex flex-wrap items-center justify-between gap-2 bg-slate-50 px-4 py-3 dark:bg-slate-800">
                  <div>
                    <div className="font-semibold text-slate-900 dark:text-white">
                      {m.nomeCompleto}
                      {m.presidente && (
                        <span className="ml-2 rounded bg-blue-100 px-1.5 py-0.5 text-[10px] font-bold uppercase text-blue-700 dark:bg-blue-950/50 dark:text-blue-300">
                          Presidente
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-slate-500 dark:text-slate-400">
                      {[m.email, m.comissaoNumero ? `Comissão ${m.comissaoNumero}` : null].filter(Boolean).join(' · ')}
                    </div>
                  </div>
                  <span className="rounded-full bg-slate-200 px-2.5 py-0.5 text-xs font-semibold text-slate-700 dark:bg-slate-700 dark:text-slate-200">
                    {m.qtdPlanos} plano(s)
                  </span>
                </div>
                <PlanosTable planos={m.planos} />
              </div>
            ))}
          </div>
        )
      ) : activeTab === 'devolvidas' ? (
        devolvidas.length === 0 ? (
          <div className={messageBoxClass}>Nenhuma emenda devolvida aguardando ciência.</div>
        ) : (
          <div className="space-y-3">
            <p className="text-sm text-slate-500 dark:text-slate-400">
              {devolvidas.length} emenda(s) devolvida(s) sem ciência
            </p>
            <div className={cardClass}>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr>
                      <th className={thClass}>Código</th>
                      <th className={thClass}>Parlamentar</th>
                      <th className={thClass}>Valor</th>
                      <th className={thClass}>Status</th>
                      <th className={thClass}>Motivo</th>
                      <th className={thClass}>Data devolução</th>
                      <th className={thClass}>Sem ciência há</th>
                    </tr>
                  </thead>
                  <tbody>
                    {devolvidas.map((d) => (
                      <tr key={d.emendaId}>
                        <td className={tdClass}>{d.codigoOficial || '—'}</td>
                        <td className={tdClass}>{d.parlamentarNome || '—'}</td>
                        <td className={tdClass}>{formatCurrency(d.valor)}</td>
                        <td className={tdClass}>{d.statusCicloVida || '—'}</td>
                        <td className={tdClass}>{d.motivoDevolucao || '—'}</td>
                        <td className={tdClass}>{formatDate(d.dataDevolucao)}</td>
                        <td className={tdClass}><DiasBadge dias={d.diasSemCiencia} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )
      ) : comCiencia.length === 0 ? (
        <div className={messageBoxClass}>Nenhuma emenda devolvida com ciência registrada.</div>
      ) : (
        <div className="space-y-3">
          <p className="text-sm text-slate-500 dark:text-slate-400">
            {comCiencia.length} emenda(s) devolvida(s) com ciência registrada
          </p>
          <div className={cardClass}>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr>
                    <th className={thClass}>Código</th>
                    <th className={thClass}>Parlamentar</th>
                    <th className={thClass}>Valor</th>
                    <th className={thClass}>Data devolução</th>
                    <th className={thClass}>Ciência por</th>
                    <th className={thClass}>Data da ciência</th>
                  </tr>
                </thead>
                <tbody>
                  {comCiencia.map((d) => (
                    <React.Fragment key={d.emendaId}>
                      <tr>
                        <td className={tdClass}>{d.codigoOficial || '—'}</td>
                        <td className={tdClass}>{d.parlamentarNome || '—'}</td>
                        <td className={tdClass}>{formatCurrency(d.valor)}</td>
                        <td className={tdClass}>{formatDate(d.dataDevolucao)}</td>
                        <td className={tdClass}>{d.usuarioCiencia || '—'}</td>
                        <td className={tdClass}>{formatDate(d.dataCiencia)}</td>
                      </tr>
                      <tr>
                        <td colSpan={6} className="border-b border-slate-200 bg-slate-50 px-3 py-3 text-sm leading-relaxed text-slate-700 dark:border-slate-700 dark:bg-slate-800/60 dark:text-slate-200">
                          <span className="font-semibold text-slate-900 dark:text-white">Motivo:</span>{' '}
                          {d.motivoDevolucao || '—'}
                        </td>
                      </tr>
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RelatorioPendenciasPage;
